import { Color } from "three";
import { MAX_ENERGY, type AtmosphereUniforms } from "./atmosphere.ts";

export { MAX_ENERGY };

/** Fração da vida em que a força sobe (depois cai ao quadrado, como as ondas de choque). */
const ATTACK = 0.12;
/** Distância (m) em que um evento vale metade para a disputa das vagas (Gate B4): perto do jogador ganha. */
const FOCUS_M = 30;

/** Os uniforms que o buffer escreve (um subconjunto da atmosfera). */
export type EnergyUniforms = Pick<AtmosphereUniforms, "uEnergy" | "uEnergyColor" | "uEnergyCount">;

interface Slot {
  x: number;
  z: number;
  radius: number;
  color: Color;
  start: number;
  ms: number;
  peak: number;
  /** Quem e o quê (`dono:habilidade`), para `cancel`; sem etiqueta, só o tempo apaga. */
  tag: string | undefined;
}

/**
 * Eventos espaciais de energia (pesquisa 11, §5): habilidades e impactos acendem por pouco tempo as
 * arestas e os nós do chão e da geometria vizinha, na cor do dono. Buffer limitado (`MAX_ENERGY`
 * vagas): cheio, a disputa é pela força restante pesada pela distância ao foco (o jogador local,
 * `focus`): o evento novo toma a vaga mais fraca se vale mais que ela, e um evento longe e fraco não
 * apaga os de perto (prioridade estável: a primeira entre iguais). Cores diferentes em lugares
 * diferentes se preservam, e o shader limita a soma nas sobreposições (`energyGlow`). Nenhuma luz
 * por partícula, nenhum tinte global do terreno.
 */
export class EnergyEvents {
  private readonly uniforms: EnergyUniforms;
  private readonly slots: Slot[] = Array.from({ length: MAX_ENERGY }, () => ({ x: 0, z: 0, radius: 0, color: new Color(), start: -Infinity, ms: 1, peak: 0, tag: undefined }));
  private focusX = 0;
  private focusZ = 0;
  private focused = false;

  constructor(uniforms: EnergyUniforms) {
    this.uniforms = uniforms;
  }

  /** O ponto que importa (o jogador local), a cada quadro; sem foco, todos os lugares valem igual. */
  focus(x: number, z: number): void {
    this.focusX = x;
    this.focusZ = z;
    this.focused = true;
  }

  /** Peso de um lugar na disputa: 1 no foco, 1/2 a `FOCUS_M` m. */
  private near(x: number, z: number): number {
    return this.focused ? 1 / (1 + ((x - this.focusX) ** 2 + (z - this.focusZ) ** 2) / (FOCUS_M * FOCUS_M)) : 1;
  }

  /** A força de uma vaga em `now` (0 fora da vida): sobe em `ATTACK` da vida e cai ao quadrado. */
  private strength(s: Slot, now: number): number {
    const k = (now - s.start) / s.ms;
    if (k <= 0 || k >= 1) return 0;
    const fall = (1 - k) / (1 - ATTACK);
    return s.peak * (k < ATTACK ? k / ATTACK : Math.min(1, fall * fall));
  }

  /**
   * Um evento em (x, z) a partir de `start` (ms; pode ser no futuro — o impacto no fim de uma
   * linha), com o raio (m), a cor do dono e a vida. `peak` (0 a 1) é a força no pico ("Efeitos dos
   * aliados" entra por aqui). Com as vagas cheias, toma a de menor força restante pesada pela
   * distância ao foco, se o novo vale pelo menos o mesmo; senão o novo fica de fora (devolve falso). `at` é
   * o relógio REAL da inserção (padrão: o próprio `start`): a
   * expiração das vagas é avaliada por ele, não pelo início futuro — senão um impacto agendado
   * apagaria um evento ainda vivo com vagas livres sobrando (revisão da Fase 12). `tag` marca o evento
   * para `cancel`.
   */
  add(x: number, z: number, radius: number, colorHex: string, start: number, ms: number, peak = 1, at = start, tag?: string): boolean {
    if (!(radius > 0) || !(ms > 0) || !(peak > 0)) return false;
    // Vaga livre (acabou em `at`), ou a com menos força pela frente (a marcada para depois vale a
    // cheia), pesada pela distância ao foco.
    let pick = 0;
    let weakest = Infinity;
    for (let i = 0; i < MAX_ENERGY; i++) {
      const s0 = this.slots[i]!;
      const k = (at - s0.start) / s0.ms;
      const left = k >= 1 ? -1 : s0.peak * (k < 0 ? 1 : (1 - k) * (1 - k)) * this.near(s0.x, s0.z);
      if (left < weakest) {
        weakest = left;
        pick = i;
      }
    }
    if (weakest >= 0 && Math.min(1, peak) * this.near(x, z) < weakest) return false;
    const s = this.slots[pick]!;
    s.x = x;
    s.z = z;
    s.radius = radius;
    s.color.set(colorHex);
    s.start = start;
    s.ms = ms;
    s.peak = Math.min(1, peak);
    s.tag = tag;
    return true;
  }

  /** Apaga na hora os eventos com a etiqueta (a carga cancelada); devolve quantos. */
  cancel(tag: string): number {
    let n = 0;
    for (const s of this.slots) {
      if (s.tag !== tag || s.peak <= 0) continue;
      s.start = -Infinity;
      s.peak = 0;
      s.tag = undefined;
      n++;
    }
    return n;
  }

  /** Eventos vivos em `now`. */
  alive(now: number): number {
    let n = 0;
    for (const s of this.slots) if (now >= s.start && now < s.start + s.ms) n++;
    return n;
  }

  /**
   * Escreve o quadro nos uniforms: os vivos compactados, a contagem no fim. Sem alocar. `gain`
   * reduz todos os picos ("Reduzir lampejos": o acender local fica mais contido, o tempo e a área
   * continuam informando).
   */
  update(now: number, gain = 1): void {
    const u = this.uniforms;
    let count = 0;
    for (const s of this.slots) {
      const strength = this.strength(s, now) * gain;
      if (strength <= 0) continue;
      u.uEnergy.value[count]!.set(s.x, s.z, s.radius, strength);
      u.uEnergyColor.value[count]!.copy(s.color);
      count++;
    }
    u.uEnergyCount.value = count;
  }

  /** Sai da cena: apaga tudo (os uniforms zeram no próximo `update`). */
  clear(): void {
    for (const s of this.slots) {
      s.start = -Infinity;
      s.peak = 0;
    }
    this.uniforms.uEnergyCount.value = 0;
  }
}
